import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import Button from '@cloudscape-design/components/button';
import Link from '@cloudscape-design/components/link';
import Select from '@cloudscape-design/components/select';
import Autosuggest from '@cloudscape-design/components/autosuggest';
import Table from '@cloudscape-design/components/table';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { getOrgHierarchy, OrgHierarchy, OrgNode, OrgOwnedPipeline } from '../apis/metricsApi';
import { DEFAULT_ORG, DEFAULT_TIER } from '../constants';

interface Props {
  currentAlias: string;
  onSelectPipeline: (pipeline: string) => void;
}

type CountKind = 'scanned' | 'not-scanned' | 'all' | 'missing' | 'has';

const AVATAR_COLORS = ['#16244d', '#2f5bea', '#3aa0ff', '#0b7285', '#5f3dc4', '#c2255c', '#2b8a3e'];
const Avatar: React.FC<{ alias: string; size?: number }> = ({ alias, size = 34 }) => {
  const c = AVATAR_COLORS[(alias.charCodeAt(0) || 0) % AVATAR_COLORS.length];
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: c, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.42, flex: '0 0 auto' }}>
      {alias.slice(0, 1).toUpperCase()}
    </div>
  );
};

// A small clickable stat chip.
const TONES = {
  grey: { bg: '#eaeded', fg: '#414d5c' },
  blue: { bg: '#e6f0ff', fg: '#0972d3' },
  red: { bg: '#fdeceb', fg: '#d13212' },
  green: { bg: '#e7f4ea', fg: '#037f51' },
  yellow: { bg: '#fff5d6', fg: '#8a6100' },
};
const Chip: React.FC<{ label: string; tone: keyof typeof TONES; onClick?: () => void; title?: string }> = ({ label, tone, onClick, title }) => (
  <span
    onClick={onClick}
    title={title}
    style={{ background: TONES[tone].bg, color: TONES[tone].fg, borderRadius: 14, padding: '3px 10px', fontSize: 13, fontWeight: 600, cursor: onClick ? 'pointer' : 'default', whiteSpace: 'nowrap', textDecoration: onClick ? 'underline' : 'none' }}
  >
    {label}
  </span>
);

// A pipeline row enriched with the owner alias for display / CSV.
type OwnedRow = OrgOwnedPipeline & { owner: string };

// Gather every pipeline under a node's subtree, tagging each with its immediate owner.
function collectPipelines(alias: string, nodes: Record<string, OrgNode>, seen = new Set<string>()): OwnedRow[] {
  const node = nodes[alias];
  if (!node || seen.has(alias)) return [];
  seen.add(alias);
  let list: OwnedRow[] = node.ownedPipelines.map(p => ({ ...p, owner: alias }));
  for (const r of node.directReports) list = list.concat(collectPipelines(r, nodes, seen));
  return list;
}

// Build the manager chain for an owner ("owner->mgr->...->aagiwal").
function chainFor(owner: string, nodes: Record<string, OrgNode>): string {
  const chain: string[] = [];
  let cur: string | undefined = owner;
  const guard = new Set<string>();
  while (cur && !guard.has(cur) && chain.length < 20) {
    guard.add(cur);
    chain.push(cur);
    cur = nodes[cur]?.manager || undefined;
  }
  return chain.join('->');
}

// Minimal CSV escaping (RFC-4180 style).
function csvCell(v: string | number | boolean | undefined | null): string {
  const s = v === undefined || v === null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function download(name: string, text: string) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.style.display = 'none';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const OrgHierarchyView: React.FC<Props> = ({ currentAlias, onSelectPipeline }) => {
  const [org, setOrg] = useState(DEFAULT_ORG);
  const [tier, setTier] = useState(DEFAULT_TIER);
  const [scannedFilter, setScannedFilter] = useState<'all' | 'scanned' | 'not-scanned'>('scanned');
  const [data, setData] = useState<OrgHierarchy | null>(null);
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState('');
  const [detail, setDetail] = useState<{ alias: string; kind: CountKind } | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    getOrgHierarchy(org, tier, currentAlias)
      .then(h => {
        if (ignore) return;
        setData(h);
        if (h) {
          // Deep link: honor ?owner=<alias> when it exists in this hierarchy, else the
          // server-resolved focus for the current user.
          const owner = searchParams.get('owner');
          setFocus(owner && h.nodes[owner] ? owner : h.focus);
          // Restore an open pipeline-list panel from ?detail=<alias>&kind=<kind>.
          const detailAlias = searchParams.get('detail');
          const kindParam = searchParams.get('kind') as CountKind | null;
          setDetail(detailAlias && h.nodes[detailAlias] && kindParam ? { alias: detailAlias, kind: kindParam } : null);
        } else {
          setDetail(null);
        }
      })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [org, tier, currentAlias]);

  // React to ?owner= changes after load (shared URL, browser back/forward).
  useEffect(() => {
    const owner = searchParams.get('owner');
    if (owner && data?.nodes?.[owner] && owner !== focus) { setFocus(owner); setDetail(null); }
  }, [searchParams, data]);

  // Reflect the open pipeline-list panel (owner + kind) in the URL so it can be shared /
  // deep-linked. Guarded on `data` so it can't wipe restored params before the first load.
  useEffect(() => {
    if (!data) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (detail) { next.set('detail', detail.alias); next.set('kind', detail.kind); }
      else { next.delete('detail'); next.delete('kind'); }
      return next;
    }, { replace: true });
  }, [detail]);

  // Keep an open detail table in sync with the scanned filter: switching the filter
  // re-points the listed pipelines (scanned / not-scanned / all) without needing a re-click.
  useEffect(() => {
    setDetail(d => {
      if (!d) return d;
      const kind: CountKind = scannedFilter === 'not-scanned' ? 'not-scanned' : scannedFilter === 'scanned' ? 'scanned' : 'all';
      return { ...d, kind };
    });
  }, [scannedFilter]);

  const nodes = data?.nodes || {};
  const focusNode: OrgNode | undefined = nodes[focus];

  const goTo = (alias: string) => {
    setFocus(alias);
    setDetail(null);
    // Reflect the focused person in the URL so it can be shared / deep-linked.
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('owner', alias);
      return next;
    }, { replace: false });
  };

  // Every alias that appears in the loaded tree — used by the alias search.
  const aliasOptions = React.useMemo(
    () => Object.keys(nodes).sort().map(a => ({ value: a })),
    [nodes],
  );
  const submitSearch = (value: string) => {
    const q = value.trim().toLowerCase();
    if (!q) return;
    // Exact match first, then any alias containing the query as a substring.
    const exact = Object.keys(nodes).find(a => a.toLowerCase() === q);
    const hit = exact || Object.keys(nodes).find(a => a.toLowerCase().includes(q));
    if (hit) { goTo(hit); setSearchValue(''); }
  };

  const ancestors: string[] = [];
  if (focusNode) {
    let m = focusNode.manager || undefined;
    let steps = 0;
    while (m && steps < 2) {
      ancestors.unshift(m);
      if (m === (data?.root || 'aagiwal')) break;
      m = nodes[m]?.manager || undefined;
      steps++;
    }
  }
  const reports = focusNode
    ? [...focusNode.directReports].sort((a, b) => (nodes[b]?.missingBusinessMetrics || 0) - (nodes[a]?.missingBusinessMetrics || 0))
    : [];

  const orgOptions = (data?.orgs || [DEFAULT_ORG]).map(o => ({ label: o, value: o }));
  const tierOptions = (data?.tiers || [DEFAULT_TIER]).map(t => ({ label: t, value: t }));
  const scannedOptions = [
    { label: 'All (scanned + not)', value: 'all' },
    { label: 'Scanned', value: 'scanned' },
    { label: 'Not scanned', value: 'not-scanned' },
  ];
  const matchesScanned = (p: OrgOwnedPipeline) =>
    scannedFilter === 'all' ? true : scannedFilter === 'scanned' ? p.scanned : !p.scanned;
  // Predicate for the leaf-owner inline table (only the Scanned filter applies).
  const matchesFilters = matchesScanned;

  // Right-side clickable counts. When the detail table is open we shrink to just the number
  // (with a tooltip carrying the full label) so the hierarchy row stays compact.
  const compact = detail !== null;
  // `filterMode` is used for a leaf owner whose pipelines are already listed inline below:
  // there, the scanned/not-scanned chips drive that table's scanned filter instead of opening
  // a redundant side panel, and the business-metrics chips become read-only counts
  // (per-pipeline status is already in the inline table's "Business metrics" column).
  const CountChips: React.FC<{ alias: string; node: OrgNode; filterMode?: boolean }> = ({ alias, node, filterMode }) => {
    const notScanned = node.total - node.scanned;
    const scannedClick = filterMode ? () => setScannedFilter('scanned') : () => setDetail({ alias, kind: 'scanned' });
    const notScannedClick = filterMode ? () => setScannedFilter('not-scanned') : () => setDetail({ alias, kind: 'not-scanned' });
    const missingClick = filterMode ? undefined : () => setDetail({ alias, kind: 'missing' });
    // When filtering to "Not scanned", show only the not-scanned count — business-metrics
    // chips don't apply because not-scanned pipelines have no business-metrics data.
    if (scannedFilter === 'not-scanned') {
      return (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Chip tone="grey" label={compact ? `${notScanned}` : `${notScanned} not scanned`} onClick={notScannedClick} title={`${notScanned} not scanned pipelines`} />
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Chip tone="blue" label={compact ? `${node.scanned}` : `${node.scanned} scanned`} onClick={scannedClick} title={`${node.scanned} scanned pipelines`} />
        {(
          node.recommendations === undefined ? (
            // Older backend without recommendation counts — keep the previous chip.
            <Chip tone="red" label={compact ? `${node.missingBusinessMetrics}` : `${node.missingBusinessMetrics} missing business metrics`} onClick={missingClick} title={`${node.missingBusinessMetrics} scanned pipelines missing business metrics`} />
          ) : (
            <Chip tone="yellow" label={compact ? `${node.recommendations}` : `${node.recommendations} recommendations`} onClick={missingClick} title={`${node.recommendations} recommended metrics across ${node.missingBusinessMetrics} scanned pipelines missing business metrics`} />
          )
        )}
      </div>
    );
  };

  // All rows in the open person's subtree (used for both the detail table and the CSV export).
  const subtreeAll: OwnedRow[] = detail ? collectPipelines(detail.alias, nodes) : [];
  const detailPipelines: OwnedRow[] = (() => {
    if (!detail) return [];
    if (detail.kind === 'all') return subtreeAll;
    if (detail.kind === 'scanned') return subtreeAll.filter(p => p.scanned);
    if (detail.kind === 'not-scanned') return subtreeAll.filter(p => !p.scanned);
    if (detail.kind === 'has') return subtreeAll.filter(p => p.scanned && p.businessMetrics);
    return subtreeAll.filter(p => p.scanned && !p.businessMetrics); // missing
  })();
  const detailLabel = detail ? ({ scanned: 'Scanned pipelines', 'not-scanned': 'Not scanned pipelines', all: 'All pipelines', has: 'Pipelines with business metrics', missing: 'Pipelines missing business metrics' } as const)[detail.kind] : '';

  const downloadCsv = () => {
    if (!detail) return;
    // Export exactly what the panel shows (the filtered list), not the whole subtree.
    const header = ['pipeline', 'owner', 'ownership_chain', 'tier', 'business_metrics', 'scanned', 'scanned_date'];
    const lines = [header.join(',')];
    for (const p of detailPipelines) {
      lines.push([p.pipeline, p.owner, chainFor(p.owner, nodes), tier, p.businessMetrics ? 'yes' : 'no', p.scanned ? 'yes' : 'no', p.scannedDate || ''].map(csvCell).join(','));
    }
    download(`${detail.alias}_${detail.kind}_pipelines_${org}_${tier}.csv`, lines.join('\n'));
  };

  // Download every pipeline currently rendered in the tree (whole loaded Org + tier),
  // deduped by pipeline in case a pipeline is reachable from multiple paths.
  const downloadAllCsv = () => {
    if (!data) return;
    const rowByPipeline = new Map<string, OwnedRow>();
    for (const [alias, n] of Object.entries(nodes)) {
      for (const p of n.ownedPipelines) {
        if (!rowByPipeline.has(p.pipeline)) rowByPipeline.set(p.pipeline, { ...p, owner: alias });
      }
    }
    const header = ['pipeline', 'owner', 'ownership_chain', 'org', 'tier', 'business_metrics', 'scanned', 'scanned_date'];
    const lines = [header.join(',')];
    for (const p of rowByPipeline.values()) {
      lines.push([p.pipeline, p.owner, chainFor(p.owner, nodes), org, tier, p.businessMetrics ? 'yes' : 'no', p.scanned ? 'yes' : 'no', p.scannedDate || ''].map(csvCell).join(','));
    }
    download(`all_pipelines_${org}_${tier}.csv`, lines.join('\n'));
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Business-metrics coverage across the org. Click a person to focus their team; click a count to list those pipelines."
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              <div style={{ minWidth: 220 }}>
                <Autosuggest
                  value={searchValue}
                  onChange={({ detail: d }) => setSearchValue(d.value)}
                  onSelect={({ detail: d }) => submitSearch(d.value)}
                  options={aliasOptions}
                  placeholder="Search alias (owner or in chain)"
                  ariaLabel="Search alias"
                  filteringType="auto"
                  enteredTextLabel={v => `Jump to "${v}"`}
                  empty="No matching alias"
                />
              </div>
              <Select selectedOption={{ label: org, value: org }} onChange={({ detail: d }) => setOrg(d.selectedOption.value || DEFAULT_ORG)} options={orgOptions} ariaLabel="Org filter" />
              <Select selectedOption={{ label: tier, value: tier }} onChange={({ detail: d }) => setTier(d.selectedOption.value || DEFAULT_TIER)} options={tierOptions} ariaLabel="Tier filter" />
              <Select selectedOption={scannedOptions.find(o => o.value === scannedFilter) || scannedOptions[0]} onChange={({ detail: d }) => setScannedFilter((d.selectedOption.value as 'all' | 'scanned' | 'not-scanned') || 'all')} options={scannedOptions} ariaLabel="Scanned filter" />
              <Button iconName="download" onClick={downloadAllCsv} disabled={!data}>Download all</Button>
            </SpaceBetween>
          }
        >
          Org hierarchy — {org} · {tier}
        </Header>
      }
    >
      {loading ? (
        <Box textAlign="center" padding="l"><Spinner /> Loading hierarchy…</Box>
      ) : !data || !focusNode ? (
        <Box color="text-body-secondary" padding="s">No hierarchy data for {org} · {tier}.</Box>
      ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* LEFT: hierarchy — narrower when detail table is open */}
          <div style={{ flex: detail ? '0 0 34%' : '1 1 100%', minWidth: 0 }}>
            {ancestors.map((a, i) => (
              <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', paddingLeft: i * 16 }}>
                <Avatar alias={a} size={26} />
                <Link onFollow={(e) => { e.preventDefault(); goTo(a); }}><b>{a}</b></Link>
              </div>
            ))}

            {/* Focus card */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, marginTop: 6, marginLeft: Math.min(ancestors.length, 2) * 16, background: 'var(--color-background-status-info, #f0f7ff)', borderRadius: 10 }}>
              <Avatar alias={focus} size={44} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Box fontSize="heading-m" fontWeight="bold">{focus}</Box>
                <Box fontSize="body-s" color="text-body-secondary">{focusNode.total} pipelines</Box>
              </div>
              <CountChips alias={focus} node={focusNode} filterMode={reports.length === 0 && focusNode.ownedPipelines.length > 0} />
            </div>

            {/* Leaf owner: no direct reports → list their pipelines inline. */}
            {reports.length === 0 && focusNode.ownedPipelines.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Box variant="awsui-key-label">Pipelines owned by {focus} ({focusNode.ownedPipelines.filter(matchesFilters).length})</Box>
                  <div style={{ marginLeft: 'auto' }}>
                    <Button iconName="download" onClick={() => {
                      const header = ['pipeline', 'owner', 'ownership_chain', 'tier', 'business_metrics', 'scanned', 'scanned_date'];
                      const lines = [header.join(',')];
                      for (const p of focusNode.ownedPipelines.filter(matchesFilters)) {
                        lines.push([p.pipeline, focus, chainFor(focus, nodes), tier, p.businessMetrics ? 'yes' : 'no', p.scanned ? 'yes' : 'no', p.scannedDate || ''].map(csvCell).join(','));
                      }
                      download(`${focus}_pipelines_${org}_${tier}.csv`, lines.join('\n'));
                    }}>Download CSV</Button>
                  </div>
                </div>
                <div style={{ maxHeight: 15 * 44, overflowY: 'auto', border: '1px solid var(--color-border-divider-default)', borderRadius: 8, marginTop: 6 }}>
                  <Table
                    variant="embedded"
                    items={focusNode.ownedPipelines.filter(matchesFilters)}
                    empty={<Box color="text-body-secondary" padding="s">No pipelines match this filter.</Box>}
                    columnDefinitions={[
                      { id: 'pipeline', header: 'Pipeline', cell: (p: OrgOwnedPipeline) => <Link onFollow={(e) => { e.preventDefault(); onSelectPipeline(p.pipeline); }}>{p.pipeline}</Link> },
                      { id: 'owner', header: 'Owner', cell: () => focus },
                      { id: 'scanned', header: 'Scanned', cell: (p: OrgOwnedPipeline) => p.scanned ? <Badge color="blue">scanned</Badge> : <Badge color="grey">not scanned</Badge> },
                      { id: 'scannedDate', header: 'Scanned date', cell: (p: OrgOwnedPipeline) => p.scannedDate || '—' },
                      { id: 'bm', header: 'Metrics', cell: (p: OrgOwnedPipeline) => !p.scanned ? <Box color="text-body-secondary">—</Box> : p.businessMetrics ? <Badge color="green">API, Infra, Business</Badge> : <Badge color="grey">Infra, API</Badge> },
                      { id: 'recs', header: 'Recommendations', cell: (p: OrgOwnedPipeline) => !p.scanned || p.recommendations === undefined ? <Box color="text-body-secondary">—</Box> : p.recommendations },
                    ]}
                    sortingDisabled
                  />
                </div>
              </div>
            )}

            {/* Direct reports */}
            {reports.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <Box variant="awsui-key-label">Direct reports ({reports.length})</Box>
                <div style={{ borderLeft: '2px solid var(--color-border-divider-default)', marginLeft: 8, paddingLeft: 12, marginTop: 6 }}>
                  {reports.map(r => {
                    const n = nodes[r];
                    if (!n) return null;
                    return (
                      <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px', borderRadius: 8, background: n.missingBusinessMetrics > 0 ? 'var(--color-background-status-error, #fff5f5)' : 'transparent' }}>
                        <Avatar alias={r} size={32} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Link onFollow={(e) => { e.preventDefault(); goTo(r); }}><b>{r}</b></Link>
                          <Box fontSize="body-s" color="text-body-secondary">{n.total} pipelines{n.directReports.length > 0 ? ` · ${n.directReports.length} reports` : ''}</Box>
                        </div>
                        <CountChips alias={r} node={n} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: pipeline detail table — wider, scrollable, ~15 rows visible */}
          {detail && (
            <div style={{ flex: '1 1 66%', minWidth: 0, borderLeft: '1px solid var(--color-border-divider-default)', paddingLeft: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Box variant="h3">{detail.alias} — {detailLabel} ({detailPipelines.length})</Box>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <Button iconName="download" onClick={downloadCsv}>Download CSV</Button>
                  <Button iconName="close" onClick={() => setDetail(null)}>Close</Button>
                </div>
              </div>
              {detailPipelines.length > 15 && (
                <Box fontSize="body-s" color="text-body-secondary" padding={{ bottom: 'xxs' }}>
                  Showing 15 rows at a time — scroll to see the remaining {detailPipelines.length - 15}.
                </Box>
              )}
              <div style={{ maxHeight: 15 * 44, overflowY: 'auto', border: '1px solid var(--color-border-divider-default)', borderRadius: 8 }}>
                <Table
                  variant="embedded"
                  items={detailPipelines}
                  empty={<Box color="text-body-secondary" padding="s">No pipelines.</Box>}
                  columnDefinitions={[
                    { id: 'pipeline', header: 'Pipeline', cell: (p: OwnedRow) => <Link onFollow={(e) => { e.preventDefault(); onSelectPipeline(p.pipeline); }}>{p.pipeline}</Link> },
                    { id: 'owner', header: 'Owner', cell: (p: OwnedRow) => p.owner },
                    { id: 'scanned', header: 'Scanned', cell: (p: OwnedRow) => p.scanned ? <Badge color="blue">scanned</Badge> : <Badge color="grey">not scanned</Badge> },
                    { id: 'scannedDate', header: 'Scanned date', cell: (p: OwnedRow) => p.scannedDate || '—' },
                    { id: 'bm', header: 'Metrics', cell: (p: OwnedRow) => !p.scanned ? <Box color="text-body-secondary">—</Box> : p.businessMetrics ? <Badge color="green">API, Infra, Business</Badge> : <Badge color="grey">Infra, API</Badge> },
                      { id: 'recs', header: 'Recommendations', cell: (p: OwnedRow) => !p.scanned || p.recommendations === undefined ? <Box color="text-body-secondary">—</Box> : p.recommendations },
                  ]}
                  sortingDisabled
                />
              </div>
            </div>
          )}
        </div>
      )}
    </Container>
  );
};
